/**
 * RealisticPinControlsPanel — sliders en vivo para los pins de la vista 3D.
 *
 * Solo se renderiza en `mapMode === 'realistic'` (decidido en
 * `SantaColomaMapView`). Modifica el store `usePinControls`; el módulo
 * `realisticCabinetMarkers` está suscrito y regenera los `PinElement` de
 * los 79 markers cuando los valores cambian.
 *
 * Mismo lenguaje visual que los sliders del editor admin del polígono
 * (`tools/auth-gateway/static/polygon-editor.html`): grid de 3 columnas
 * (label · slider · valor), accent cyan, indicador numérico monoespaciado
 * a la derecha.
 */
import { Sliders } from 'lucide-react';
import { useState } from 'react';
import { usePinControls } from '../../store/realisticPinControls';

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}

function SliderRow({ label, value, min, max, step, onChange, format }: SliderRowProps) {
  return (
    <div className="grid grid-cols-[60px_1fr_36px] gap-2 items-center">
      <label className="text-[11px] text-gray-400">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-cyan-500 cursor-pointer"
      />
      <span className="text-[11px] font-mono text-gray-300 text-right">
        {format ? format(value) : value.toFixed(2)}
      </span>
    </div>
  );
}

interface ColorRowProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
}

function ColorRow({ label, value, onChange }: ColorRowProps) {
  return (
    <div className="grid grid-cols-[60px_1fr_56px] gap-2 items-center">
      <label className="text-[11px] text-gray-400">{label}</label>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-6 bg-transparent cursor-pointer rounded border border-gray-700"
      />
      <span className="text-[10px] font-mono text-gray-300 text-right uppercase">
        {value}
      </span>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ label, value, onChange }: ToggleRowProps) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-[11px] transition-colors ${
        value
          ? 'bg-cyan-900/30 border border-cyan-700/50 text-cyan-300'
          : 'bg-gray-800/40 border border-gray-700/50 text-gray-400 hover:bg-gray-800/70'
      }`}
    >
      <span>{label}</span>
      <span className={`text-[10px] font-mono ${value ? 'text-cyan-400' : 'text-gray-500'}`}>
        {value ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}

export function RealisticPinControlsPanel() {
  const {
    pinOpacity, glyphOpacity, pinScale, glyphColor, modelColor, modelScale,
    regulatorsEnabled, lightsEnabled, autoScale, lightAltitude, lightIdsVisible, trafficFlowEnabled,
    setPinOpacity, setGlyphOpacity, setPinScale, setGlyphColor, setModelColor, setModelScale,
    setRegulatorsEnabled, setLightsEnabled, setAutoScale, setLightAltitude, setLightIdsVisible, setTrafficFlowEnabled,
  } = usePinControls();
  const [open, setOpen] = useState(true);

  return (
    <div className="absolute top-3 left-3 z-40">
      {open ? (
        <div className="bg-gray-900/90 backdrop-blur-md border border-gray-700/60 rounded-lg shadow-xl p-3 w-[260px]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Sliders className="w-3 h-3 text-cyan-400" />
              <span className="text-[11px] font-semibold text-gray-300">Config</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[10px] text-gray-500 hover:text-gray-300"
              title="Ocultar panel"
            >
              ✕
            </button>
          </div>
          <div className="space-y-1.5 mb-2">
            <ToggleRow label="Reguladores" value={regulatorsEnabled} onChange={setRegulatorsEnabled} />
            <ToggleRow label="Semáforos"   value={lightsEnabled}     onChange={setLightsEnabled} />
            <ToggleRow label="Auto-escala" value={autoScale}         onChange={setAutoScale} />
            <ToggleRow label="IDs sem."    value={lightIdsVisible}   onChange={setLightIdsVisible} />
            <ToggleRow label="Tráfico"     value={trafficFlowEnabled} onChange={setTrafficFlowEnabled} />
          </div>
          <div className="h-px bg-gray-700/60 my-2" />
          <div className="space-y-2">
            <SliderRow
              label="Opac. pin"
              value={pinOpacity}
              min={0}
              max={1}
              step={0.05}
              onChange={setPinOpacity}
            />
            <SliderRow
              label="Opac. núm."
              value={glyphOpacity}
              min={0}
              max={1}
              step={0.05}
              onChange={setGlyphOpacity}
            />
            <SliderRow
              label="Tamaño"
              value={pinScale}
              min={0.5}
              max={2.5}
              step={0.1}
              onChange={setPinScale}
              format={(v) => v.toFixed(1)}
            />
            <SliderRow
              label="Mod. ×"
              value={modelScale}
              min={1}
              max={30}
              step={1}
              onChange={setModelScale}
              format={(v) => autoScale ? 'auto' : `${v.toFixed(0)}×`}
            />
            <SliderRow
              label="Altura sem."
              value={lightAltitude}
              min={0}
              max={8}
              step={0.25}
              onChange={setLightAltitude}
              format={(v) => `${v.toFixed(2)} m`}
            />
            <div className="h-px bg-gray-700/60 my-1" />
            <ColorRow label="Color núm." value={glyphColor} onChange={setGlyphColor} />
            <ColorRow label="Color mod." value={modelColor} onChange={setModelColor} />
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="bg-gray-900/90 backdrop-blur-sm border border-gray-700 rounded-lg p-2 hover:bg-gray-800/90 transition-colors"
          title="Sliders de pins"
        >
          <Sliders className="w-3.5 h-3.5 text-cyan-400" />
        </button>
      )}
    </div>
  );
}
